/**
 * The creative detail rail's metric model — pure, DOM-free, so the whole "what does the panel show
 * and when" contract is unit-testable without a query client.
 *
 * The panel reads as a DIAGNOSIS, in four groups: what the creative returned (**Outcome**), how it
 * was delivered (**Delivery**), where the click journey broke (**Click journey**), and what about
 * the creative itself explains it (**Creative diagnostics**). That order is
 * the order a buyer asks the questions in; the previous grouping mirrored the warehouse's column list
 * instead, which answered "what does Meta report?".
 *
 * Three rules are encoded here rather than in the component.
 *
 * **Source.** Every metric declares whether it comes from the grid ROW the modal was opened with, or
 * from the additive DETAIL read. Row metrics render synchronously — the client already holds them, so
 * putting a spinner over them would be a lie. Only detail metrics can be pending, and they skeleton
 * individually rather than blanking their group. When the panel is read over a window OTHER than the
 * grid's, the row describes a different period, so the outcome figures come from the detail read
 * instead and are marked `detail` accordingly.
 *
 * **Basis.** Every rate states its denominator in its subline. Two of them changed with this model —
 * hook rate now divides three-second plays by impressions, and hold rate divides ThruPlays by
 * three-second plays rather than by impressions — and a rate whose denominator is not stated is a
 * rate nobody can check.
 *
 * **Tone.** The grid's metrics reuse the grid's own ABSOLUTE tone rule, so a creative reads
 * identically in the card and in the modal opened from it. Everything else is either neutral, carries
 * a published band from `shared` (see `creative-detail-benchmarks.ts`), or uses a shared soft rule
 * (`AOV_MATERIAL_MULTIPLE` for the Outcome AOV nod) — the panel never invents a threshold of its own.
 *
 * **Seven metrics were deliberately removed** and are NOT to be added back without a reason:
 * `Clicks (all)`, `CTR (all)` and `CPC (all)` count every tap on the unit including likes and profile
 * taps, so they inflate against the outbound click that actually matters; `Link clicks` is the same
 * event as `Outbound clicks` on a web-conversion ad; `Cost / result` on a purchase-optimised creative
 * is `Cost / purchase` under a second name; `Post engagement` judges nothing; `2-second views` is a
 * weaker duplicate of the three-second play. They are all still returned by the route — the
 * media-metrics table and the agent read them.
 */
import { AOV_MATERIAL_MULTIPLE, DIRECTIONAL_PURCHASES_FLOOR, FUNNEL_THRESHOLDS, VERDICT_PURCHASES_FLOOR, type ICreativeBandKey } from 'shared'

export { AOV_MATERIAL_MULTIPLE }

import { formatSpend } from '@/features/creative-intelligence/utils/creative-intelligence-format'
import type { ICreativePerformanceRow } from '@/api/creative-intelligence/get-creative-performance-query'
import type { ICreativeDetail } from '@/api/creative-intelligence/get-creative-detail-query'
import type { IMetricTone, IMetricToneResult } from '@/features/creative-performance/creative-performance-tone'
import {
    rampForColumn,
    stampToneContext,
    toneForTableColumn,
    type IMetricToneContext,
} from '@/features/creative-performance/creative-performance-tone'
import type { IMetricAnchors } from 'shared'
import {
    GRID_METRIC_BY_KEY,
    CREATIVE_METRIC_BY_GRID_KEY,
    type IGridMetricKey,
} from '@/features/creative-performance/creative-performance-metrics'
import { formatContribution } from '@/features/creative-performance/creative-performance-contributions'
import {
    costPerResultTypeLabel,
    resultTypeLabel,
    resultTypeSingularLabel,
} from '@/features/agents/agent-panel/agent-panel-employees/channel-insights-result-type-label'
import { outcomeMetricLabels } from '@/features/creative-performance/creative-performance-metrics'
import type { IFunnelStage } from '@/features/creative-performance/creative-detail/creative-detail-funnel-chart'

import {
    directionalDisclosure,
    evaluateBand,
    type IBandBadge,
    type IBandContext,
} from '@/features/creative-performance/creative-detail/creative-detail-benchmarks'
import type { IMetricTooltip, IMetricTooltipSection } from '@/features/creative-performance/creative-detail/creative-detail-type-scale'

/** Where a metric's number comes from — decides whether it can ever show a loading state. */
export type IDetailMetricSource = 'row' | 'detail'

export interface IDetailMetric {
    key: string
    label: string
    /**
     * Everything a reader might need about this metric, in ONE hover: the formula, the band it is read
     * against, the comparison behind its suffix, and why it is unavailable when it is.
     *
     * Sublines under a value were the panel's biggest source of visual noise — five stacked 9px greys
     * per section, each individually reasonable and collectively unreadable. They all live here now.
     * The label carries a dotted underline so the hover is discoverable. Band ranges belong here too —
     * never beside the value.
     */
    tooltip?: IMetricTooltip
    /** Formatted value, or `null` when a `detail`-sourced metric has not loaded yet. */
    value: string | null
    tone: IMetricTone
    /** Why this tone — the chip's tooltip, so the band is stated rather than inferred. */
    reason?: string
    /**
     * Continuous account-relative tint 0..1 when W/B anchors apply — same ramp the card/table use.
     * Rendered as a soft emerald wash with dark emerald ink.
     */
    ramp?: number
    /**
     * A SHORT status chip beside the value — e.g. `directional — n = 4 < 5`.
     * Published band ranges are NOT badges; those live only in the tooltip.
     */
    badge?: string
    /**
     * A SHORT comparison on the value's own baseline — `vs 1.9x`, `vs €48 avg`, `11% of campaign`.
     *
     * Deliberately not a subline: a suffix on the same line is read as part of the number, while text
     * underneath is read as another row. Anything longer than a few words belongs in the tooltip.
     */
    context?: string
    /**
     * How loudly to render this metric.
     *
     * `hero` gets a display-size number: the four figures a buyer decides on. `supporting` gets a
     * smaller tile — real, but read second. `detail` is collapsed behind a disclosure, because a
     * panel where everything is emphasised has no emphasis at all. Delivery is the whole `detail`
     * tier: CPM and frequency matter, but nobody opens a creative to read them first.
     */
    tier: IDetailMetricTier
    source: IDetailMetricSource
    /**
     * The account-level variables this metric is computed FROM. A metric is presented only where the
     * outcome model reports every one of them; otherwise it renders unmeasured, naming what is
     * missing.
     *
     * Declared rather than branched because the same knowledge used to live in six places at once —
     * a `sellsThroughMeta` flag, a `dashed()` wrapper, an `unmeasured` tooltip, two tier variables
     * and a per-metric spread — all encoding one fact. Declaring it makes "which metrics does a
     * lead-gen advertiser see" answerable by reading a list.
     */
    requires?: IOutcomeVariable[]
}

/** One account-level variable a metric can require. See `IDetailMetric.requires`. */
export type IOutcomeVariable = 'count' | 'value' | 'spend' | 'denominator'

/** The three levels of emphasis the panel renders. See `IDetailMetric.tier`. */
export type IDetailMetricTier = 'hero' | 'supporting' | 'detail'

export interface IDetailMetricGroup {
    key: string
    label: string
    /** A one-line qualifier on the whole group — how to read it, or why it is thin. */
    note?: string
    metrics: IDetailMetric[]
}

const UNAVAILABLE = '—'

/**
 * Build a structured hover: each non-empty part becomes its own block. Strings are unlabeled leads;
 * `{ label, body }` blocks get a small uppercase heading (Band / Reading / Note).
 *
 * Replaces the old middot-joined wall that mashed formula + band + doctrine into one paragraph.
 */
function tip(...parts: (string | IMetricTooltipSection | null | undefined | false)[]): IMetricTooltipSection[] | undefined {
    const sections: IMetricTooltipSection[] = []
    for (const part of parts) {
        if (!part) continue
        if (typeof part === 'string') sections.push({ body: part })
        else if (part.body) sections.push(part)
    }
    return sections.length ? sections : undefined
}

/** Formula + band range + reading/note — no duplicated doctrine across sections. */
function bandTip(formula: string, judged: IBandBadge | null | undefined): IMetricTooltipSection[] | undefined {
    if (!judged) return tip(formula)
    return tip(
        formula,
        { label: 'Band', body: judged.badge },
        judged.reading && !judged.withheld ? { label: 'Reading', body: judged.reading } : null,
        judged.withheld
            ? { label: 'Note', body: judged.reason }
            : judged.grounding
              ? { label: 'Note', body: judged.grounding }
              : null,
    )
}

/**
 * The advertiser's outcome, as the panel needs it — the terminal conversion event, whether it carries
 * money, and which denominator its rates may divide by. Mirrors the server's `outcomeModel`.
 */
export type IPanelOutcomeModel = NonNullable<ICreativeDetail['outcomeModel']>

/**
 * What the panel assumes before the detail read lands, and for a response cached from before the
 * model shipped: an e-commerce advertiser, exactly as the panel behaved until now.
 *
 * Degrading to the previous behaviour beats blanking the outcome block on a stale cache entry — the
 * same tolerance `buildFunnelStages` already applies to a missing `reported`.
 */
const ASSUMED_PURCHASE_MODEL: IPanelOutcomeModel = {
    terminalActionType: 'omni_purchase',
    terminalCount: 0,
    terminalValueMicros: '0',
    hasValue: true,
    isPurchase: true,
    landingPageViews: 0,
    outboundClicks: 0,
    landingPageViewRate: null,
    landingPageViewsCredible: true,
    initiatedCheckoutsCredible: true,
    preferredDenominator: 'landingPageViews',
}

/** Why a figure reads `—`: the variable behind it is not measured on this account. */
function unmeasuredReason(variable: IOutcomeVariable, model: IPanelOutcomeModel) {
    const event = model.terminalActionType ? resultTypeLabel(model.terminalActionType).toLowerCase() : 'conversion'
    if (variable === 'count') return 'This account reports no conversion events, so this is not measured here — it is not a zero.'
    if (variable === 'value') return `This account reports no value against its ${event} events, so this is not measured here — it is not a zero.`
    return 'This account measures no denominator this rate can credibly divide by, so it is withheld rather than computed against a number known to be wrong.'
}

/**
 * Which required variable the account is missing, or `null` when it has them all.
 *
 * Judged at ACCOUNT scope, never at creative scope: a creative that drove none of a MEASURED event
 * must read `0`, and only an account reporting none of it anywhere reads `—`.
 */
function missingVariable(requires: IOutcomeVariable[] | undefined, model: IPanelOutcomeModel) {
    for (const variable of requires ?? []) {
        if (variable === 'count' && !model.terminalActionType) return variable
        if (variable === 'value' && !model.hasValue) return variable
        if (variable === 'denominator' && model.preferredDenominator === null) return variable
    }
    return null
}

/**
 * Present a metric, or withhold it because the account does not measure what it is built from.
 *
 * The single gate every Outcome metric passes through — so "why is this a dash" has one answer and
 * one place to change it.
 */
function presentOrWithhold(metric: IDetailMetric, model: IPanelOutcomeModel): IDetailMetric {
    const missing = missingVariable(metric.requires, model)
    if (!missing) return metric
    return {
        ...metric,
        value: UNAVAILABLE,
        tone: 'neutral',
        reason: undefined,
        badge: undefined,
        context: undefined,
        tooltip: unmeasuredReason(missing, model),
    }
}

/**
 * The terminal event's name in each of the places the Outcome group needs it.
 *
 * Delegates to the grid's builder so the panel, the grid's cards and the table cannot name the same
 * event differently for the same creative.
 */
function outcomeLabels(model: IPanelOutcomeModel) {
    return outcomeMetricLabels(model)
}

const compact = (n: number) =>
    new Intl.NumberFormat(undefined, { notation: 'compact', maximumFractionDigits: 1 }).format(n)

/** A warehouse count string → a compact display number. */
function formatCount(value: string | number | null | undefined) {
    if (value === null || value === undefined) return UNAVAILABLE
    const n = Number(value)
    return Number.isFinite(n) ? compact(n) : UNAVAILABLE
}

/** A fraction (0.0011) → a percentage ("0.11%"). `null` reads as unavailable, never "0%". */
export function formatRate(value: number | null | undefined) {
    if (value === null || value === undefined) return UNAVAILABLE
    return `${(value * 100).toFixed(2)}%`
}

/** A fraction as a whole-ish percentage ("86%") — for the funnel step-throughs, where 2dp is noise. */
function formatShare(value: number | null | undefined) {
    if (value === null || value === undefined) return UNAVAILABLE
    return `${(value * 100).toFixed(1)}%`
}

/** Micros → money, or the unavailable dash. Kept separate from `formatSpend` so `null` never reads as 0. */
function formatMoney(micros: string | null | undefined, currency: string) {
    if (micros === null || micros === undefined) return UNAVAILABLE
    return formatSpend(Number(micros), currency)
}

/** A `row`-sourced metric: always present, never pending. */
function rowMetric(
    key: string,
    label: string,
    value: string,
    over: Partial<IDetailMetric> = {},
): IDetailMetric {
    return { key, label, value, tone: 'neutral', tier: 'supporting', source: 'row', ...over }
}

/**
 * A `detail`-sourced metric. `value` is `null` while the additive read is in flight — the component
 * renders a skeleton for exactly those cells and leaves every row-sourced figure on screen.
 */
function detailMetric(
    key: string,
    label: string,
    value: string | null,
    over: Partial<IDetailMetric> = {},
): IDetailMetric {
    return { key, label, value, tone: 'neutral', tier: 'supporting', source: 'detail', ...over }
}

/** One of the grid metrics, reusing the grid's own formatter AND its absolute + ramp tone rule. */
function gridMetric(
    key: IGridMetricKey,
    row: ICreativePerformanceRow,
    currency: string,
    toneContext: IMetricToneContext = {},
): IDetailMetric {
    const def = GRID_METRIC_BY_KEY[key]
    const judged = toneForTableColumn(key)?.(stampToneContext(row, toneContext))
    return {
        key,
        label: def.label,
        value: def.format(row, currency),
        tone: judged?.tone ?? 'neutral',
        reason: judged?.reason,
        ramp: judged?.ramp,
        tier: 'supporting',
        source: 'row',
    }
}

/**
 * Whether to present the video diagnostics at all. Keyed on what the creative IS, not on whether its
 * counts are non-zero: a video that delivered and was never watched must still show its zero funnel
 * (that is the finding), while a static image must not show one (an all-zero funnel on an image reads
 * as failure rather than absence). Mirrors the server's `hasVideoDelivery` so the group's presence is
 * known synchronously, before the detail read lands.
 */
export function hasVideoDelivery(row: ICreativePerformanceRow) {
    return row.creativeType === 'VIDEO' || row.media?.kind === 'VIDEO' || row.thumbnailIsVideo === true
}

/** A result action type as a label — `omni_purchase` → "Purchase", `''` → "Result". */
export function resultLabel(resultActionType: string) {
    if (!resultActionType) return 'Result'
    const cleaned = resultActionType.replace(/^omni_/, '').replace(/^offsite_conversion\.fb_pixel_/, '').replace(/_/g, ' ')
    return cleaned.charAt(0).toUpperCase() + cleaned.slice(1)
}

/**
 * Whether the creative's own optimisation result IS its purchase count. When it is, "Purchases" and
 * "Results" are the same number under two names, and showing both makes the panel look like it is
 * double-counting — and "Cost / result" is then "Cost / purchase" again. The purchase figures are the
 * ones that stay (they are canonical and objective-independent); the result line is dropped.
 */
export function resultsDuplicatePurchases(resultActionType: string) {
    return resultActionType.includes('purchase')
}

/** The purchase count the panel judges by — the detail read's when it governs, else the row's. */
function purchasesOf(row: ICreativePerformanceRow, outcome: ICreativeDetail['outcome'] | null) {
    return Number((outcome ? outcome.purchaseConversions : row.purchaseConversions) ?? 0)
}

/**
 * Build the full metric rail for one creative.
 *
 * `detail` is `null` while the additive read is in flight or after it failed — in both cases every
 * `row`-sourced figure is still returned, so the panel degrades to what the grid already knew rather
 * than blanking.
 *
 * `windowMatchesGrid` decides where the OUTCOME figures come from. When the panel is showing the
 * grid's own window they come from the row and paint instantly. When the user has widened the window
 * or picked "last active period", the row describes a different period, so they come from the detail
 * read — the whole panel is loading in that case anyway, and showing 7-day spend beside 30-day funnel
 * counts would be worse than a skeleton.
 */
export function buildDetailMetricGroups({
    row,
    detail,
    currency,
    windowMatchesGrid = true,
    cardMetricKeys,
    metricAnchors,
}: {
    row: ICreativePerformanceRow
    detail: ICreativeDetail | null | undefined
    currency: string
    windowMatchesGrid?: boolean
    /**
     * Metrics the grid cards currently show. Every one of these MUST appear somewhere on the panel —
     * a KPI on a card that vanishes in the modal is a broken contract. Missing keys are appended to
     * the matching panel group from the catalog's funnel stage.
     */
    cardMetricKeys?: readonly IGridMetricKey[]
    /** Trailing-90d W/B anchors — same account-relative ramps the card/table paint. */
    metricAnchors?: IMetricAnchors
}): IDetailMetricGroup[] {
    const totals = detail?.totals ?? null
    const outcome = windowMatchesGrid ? null : (detail?.outcome ?? null)
    const isVideo = hasVideoDelivery(row)
    // The ad's FORMAT, not merely "carries a video asset". Server-authoritative when the read has
    // landed; until then the row's own creative type is the same test the server applies.
    const isVideoFormat = (detail?.isVideoFormat ?? row.creativeType === 'VIDEO') === true
    const model = detail?.outcomeModel ?? ASSUMED_PURCHASE_MODEL
    const purchases = terminalCountOf(row, outcome, detail)
    const impressions = Number((outcome ? outcome.impressions : row.impressions) ?? 0)
    const bandContext: IBandContext = {
        isVideo,
        isVideoFormat,
        currency,
        impressions,
        purchases,
        isPurchaseOutcome: model.isPurchase,
    }
    const toneContext: IMetricToneContext = {
        isPurchaseOutcome: model.isPurchase,
        metricAnchors,
    }
    const conversions = conversionsGroup({
        conversions: detail?.conversions ?? null,
        currency,
        isLoading: detail === undefined,
    })
    const band = (key: Parameters<typeof evaluateBand>[0], value: number | null | undefined) =>
        evaluateBand(key, value, bandContext)

    const groups = [
        outcomeGroup({ row, outcome, detail, currency, band, windowMatchesGrid, toneContext }),
        ...(conversions ? [conversions] : []),
        deliveryGroup({ row, totals, currency, band, windowMatchesGrid, toneContext }),
        funnelGroup(),
        ...(isVideo ? [diagnosticsGroup({ totals, band, isVideoFormat, row, toneContext })] : []),
    ]
    return ensureCardMetricsOnPanel(groups, cardMetricKeys, row, currency, toneContext)
}

/**
 * Catalog funnel stage → panel group. Clicks ride with Delivery; video diagnostics need a video
 * creative (otherwise they fall onto Delivery so a selected hook-rate on a static still surfaces).
 */
const PANEL_GROUP_FOR_CATALOG_GROUP: Record<string, string> = {
    results: 'outcome',
    delivery: 'delivery',
    clicks: 'delivery',
    funnel: 'outcome',
    video: 'diagnostics',
    engagement: 'delivery',
    lifecycle: 'outcome',
    leads: 'outcome',
    appTrials: 'outcome',
}

/**
 * Append any card-selected metric the fixed panel layout omitted. The card picker and the modal
 * share one catalog — selecting a KPI on the grid must never open a modal that cannot show it.
 */
export function ensureCardMetricsOnPanel(
    groups: IDetailMetricGroup[],
    cardMetricKeys: readonly IGridMetricKey[] | undefined,
    row: ICreativePerformanceRow,
    currency: string,
    toneContext: IMetricToneContext = {},
): IDetailMetricGroup[] {
    if (!cardMetricKeys?.length) return groups
    const present = new Set(groups.flatMap((g) => g.metrics.map((m) => m.key)))
    const next = groups.map((g) => ({ ...g, metrics: [...g.metrics] }))
    const byKey = Object.fromEntries(next.map((g) => [g.key, g])) as Record<string, IDetailMetricGroup>

    for (const key of cardMetricKeys) {
        if (present.has(key)) continue
        if (!GRID_METRIC_BY_KEY[key]) continue
        const catalogGroup = CREATIVE_METRIC_BY_GRID_KEY[key]?.group
        let panelKey = (catalogGroup && PANEL_GROUP_FOR_CATALOG_GROUP[catalogGroup]) || 'outcome'
        if (panelKey === 'diagnostics' && !byKey.diagnostics) panelKey = 'delivery'
        const target = byKey[panelKey] ?? byKey.outcome
        if (!target) continue
        target.metrics.push({ ...gridMetric(key, row, currency, toneContext), tier: 'supporting' })
        present.add(key)
    }
    return next
}

/** What the creative returned. Every explanation is a hover; only the number and one suffix are drawn. */
function outcomeGroup({
    row,
    outcome,
    detail,
    currency,
    band,
    windowMatchesGrid,
    toneContext,
}: {
    row: ICreativePerformanceRow
    outcome: ICreativeDetail['outcome'] | null
    detail: ICreativeDetail | null | undefined
    currency: string
    band: (key: Parameters<typeof evaluateBand>[0], value: number | null | undefined) => ReturnType<typeof evaluateBand>
    windowMatchesGrid: boolean
    toneContext: IMetricToneContext
}): IDetailMetricGroup {
    // With the grid's own window the row IS the answer and paints instantly; over any other window it
    // describes a different period, so every outcome figure waits on the detail read.
    const src: IDetailMetricSource = windowMatchesGrid ? 'row' : 'detail'
    const pending = !windowMatchesGrid && !outcome

    const spendMicros = outcome?.spendMicros ?? row.spendMicros
    // The creative's own count of the account's terminal event. The grid row carries it for a
    // purchase advertiser; anything else comes from the conversions read, matched on the same key.
    const terminalCount = terminalCountOf(row, outcome, detail)
    const aovMicros = outcome ? outcome.aovMicros : rowAovMicros(row)
    const roasValue = outcome ? outcome.roas : row.roas
    const roasBadge = band('roas', roasValue)
    const resultActionType = outcome?.resultActionType ?? row.resultActionType
    const mixedObjectives = outcome?.mixedObjectives ?? row.mixedObjectives

    const spendShare = detail?.campaign?.spendShare ?? null
    const accountAov = detail?.accountAovMicros ?? null
    const concentration = detail?.campaign?.topTwoAdsSpendShare ?? null

    /**
     * What this advertiser's outcome IS — the event every figure below names, and whether it carries
     * the variables those figures are built from.
     *
     * Nothing is hidden on an account that measures less: hiding a metric makes a reader think the
     * panel lost it. A metric whose variable is absent renders `—` and says which variable on hover,
     * and the account's own optimisation result takes the emphasis instead.
     */
    const model = detail?.outcomeModel ?? ASSUMED_PURCHASE_MODEL
    const labels = outcomeLabels(model)
    /** The value block leads only where there is value to lead with. */
    const outcomeTier: IDetailMetricTier = model.hasValue ? 'hero' : 'supporting'
    const resultTier: IDetailMetricTier = model.hasValue ? 'supporting' : 'hero'
    /** A band calibrated on purchases judges only purchases; anything else is presented neutrally. */
    const bandsApply = model.isPurchase

    const metrics: IDetailMetric[] = [
        {
            ...(windowMatchesGrid
                ? gridMetric('spend', row, currency, toneContext)
                : detailMetric('spend', 'Spend', pending ? null : formatMoney(spendMicros, currency))),
            tier: 'hero',
            context: spendShare === null ? undefined : `${(spendShare * 100).toFixed(0)}% of campaign`,
            tooltip: tip(
                'What this creative spent in the window.',
                spendShare !== null && `${(spendShare * 100).toFixed(0)}% of the spend of the campaigns it ran in.`,
                concentration !== null &&
                    concentration >= TOP_ADS_DEPENDENCY_SHARE &&
                    `Those campaigns lean on their top two ads for ${(concentration * 100).toFixed(0)}% of spend — pausing one is a budget decision, not just a creative one.`,
            ),
        },
        presentOrWithhold(
            {
                ...(windowMatchesGrid
                    ? rowMetric('purchases', labels.count, formatCount(terminalCount))
                    : detailMetric('purchases', labels.count, pending ? null : formatCount(terminalCount))),
                tier: outcomeTier,
                requires: ['count'],
                badge: directionalDisclosure(terminalCount) ?? undefined,
                tooltip: tip(
                    model.isPurchase
                        ? "Meta's de-duplicated omni_purchase count."
                        : `How many ${labels.count.toLowerCase()} this creative drove — the account's own highest-volume conversion event.`,
                    terminalCount < DIRECTIONAL_PURCHASES_FLOOR &&
                        `Under ${DIRECTIONAL_PURCHASES_FLOOR} the ratios built on this are directional — read them as a hint, not a verdict.`,
                ),
            },
            model,
        ),
        presentOrWithhold(
            {
                // A purchase account reads the grid's own cost-per-purchase, so the panel cannot
                // disagree with the card. Anything else divides this creative's spend by its own count
                // of the terminal event — the figure the conversions block already carries.
                ...(model.isPurchase
                    ? {
                          ...(windowMatchesGrid
                              ? gridMetric('cpa', row, currency, toneContext)
                              : detailMetric('cpa', labels.cost, pending ? null : formatMoney(outcome?.costPerPurchaseMicros, currency))),
                      }
                    : detailMetric('cpa', labels.cost, formatMoney(terminalCostMicros(detail, model), currency))),
                label: labels.cost,
                tier: outcomeTier,
                requires: ['count', 'spend'],
                tooltip: tip(`Spend ÷ ${labels.count.toLowerCase()}.`),
            },
            model,
        ),
        presentOrWithhold(
            {
                ...(windowMatchesGrid
                    ? gridMetric('roas', row, currency, toneContext)
                    : detailMetric('roas', labels.ratio, pending ? null : formatRoas(outcome?.roas))),
                label: labels.ratio,
                tier: outcomeTier,
                requires: ['value', 'spend'],
                // The `vs 1.9x med` suffix is the specific thing that invites a cross-account
                // comparison, and the median behind it is purchase-derived. Dropped off a purchase.
                ...(bandsApply ? { context: 'vs 1.9x med' } : { context: undefined, tone: 'neutral' as const, reason: undefined }),
                tooltip: tip(`${labels.value} ÷ spend.`, bandsApply && roasBadge?.reason),
            },
            model,
        ),
        presentOrWithhold(
            {
                ...(windowMatchesGrid
                    ? gridMetric('purchaseValue', row, currency, toneContext)
                    : detailMetric('purchaseValue', labels.value, pending ? null : formatMoney(outcome?.revenueMicros, currency))),
                label: labels.value,
                requires: ['value'],
                tooltip: tip('Value Meta attributes to this creative in the window.'),
            },
            model,
        ),
        presentOrWithhold(
            {
                key: 'aov',
                label: labels.average,
                value: pending ? null : formatMoney(aovMicros, currency),
                tone: 'neutral',
                tier: 'supporting',
                source: src,
                requires: ['value', 'count'],
                context: accountAov ? `vs ${formatSpend(Number(accountAov), currency)} avg` : undefined,
                tooltip: tip(
                    `${labels.value} ÷ ${labels.count.toLowerCase()}.`,
                    aovContext(aovMicros, accountAov, terminalCount, currency)
                ),
            },
            model,
        ),
        presentOrWithhold(
            detailMetric('conversionRate', 'Conversion rate', funnelRate(detail, 'conversionRate'), {
                requires: ['count', 'denominator'],
                // The basis is part of the metric, not a footnote: a rate whose denominator is not
                // stated is a rate nobody can check, and this one's denominator is chosen per account.
                context: conversionRateBasisLabel(detail) ?? undefined,
                tooltip: tip(
                    `${labels.count} ÷ ${conversionRateBasisPhrase(detail)} — the AD-LEVEL pixel rate, which runs higher than a store session rate and must never be compared to one.`,
                    detail?.funnel?.conversionRateBasis === 'outboundClicks' &&
                        'Landing-page views are not a credible denominator on this account, so this divides by outbound clicks instead.',
                ),
            }),
            model,
        ),
    ]

    // A non-purchase objective keeps its own result and cost; on an advertiser that does not sell
    // through Meta they ARE the outcome, and lead.
    if (!resultsDuplicatePurchases(resultActionType)) {
        // The panel is not width-constrained, so it lists EVERY objective rather than capping at the
        // three a card can fit.
        const breakdown = outcome?.resultBreakdown ?? row.resultBreakdown ?? []
        const results = outcome?.results ?? row.results
        metrics.push({
            // The blended TOTAL stays, whatever the objectives underneath. The grid card still shows
            // it, and a panel that omitted it would leave a buyer who sorted on Results unable to find
            // the figure they clicked. Its caveat moves into the tooltip rather than into the label.
            ...(windowMatchesGrid
                ? rowMetric('results', mixedObjectives ? 'Results' : `${resultLabel(resultActionType)}s`, formatCount(results))
                : detailMetric(
                      'results',
                      mixedObjectives ? 'Results' : `${resultLabel(resultActionType)}s`,
                      pending ? null : formatCount(outcome?.results),
                  )),
            tier: resultTier,
            tooltip: tip(
                mixedObjectives
                    ? "Every objective's count added together. The parts below are the readable figures — a start trial and a purchase are not one unit, so this total has none."
                    : "The count Meta reports for this ad set's own optimisation goal.",
            ),
        })

        if (mixedObjectives && breakdown.length > 1) {
            for (const contribution of breakdown) {
                // The account's terminal event already has its own row above (`labels.count`), so a
                // contribution naming that same event would render a second row under an identical
                // label with a different number.
                if (contribution.resultActionType === model.terminalActionType) continue
                if (resultsDuplicatePurchases(contribution.resultActionType)) continue
                const singular = resultTypeSingularLabel(contribution.resultActionType) || 'Result'
                // The PLURAL is looked up, never derived: the label module stores both forms because
                // an "-s" rule renders "Adds to Cart" as "Add to Carts" and "People Reached" as
                // "Person Reacheds".
                const plural = resultTypeLabel(contribution.resultActionType) || 'Results'
                metrics.push(
                    {
                        ...(pending
                            ? detailMetric('results', plural, null)
                            : rowMetric('results', plural, formatCount(contribution.results))),
                        key: `results:${contribution.resultActionType}`,
                        tier: resultTier,
                        tooltip: tip(`The count Meta reports for the ad sets optimising for ${singular}.`),
                    },
                    {
                        // Formatted through the CARD's formatter, not `formatMoney`: the latter rounds
                        // to whole units under 1,000, so a €0.42 cost per link click would read €0 in
                        // the modal and €0.42 on the card it was opened from.
                        ...(pending
                            ? detailMetric('costPerResult', `Cost / ${singular}`, null)
                            : rowMetric(
                                  'costPerResult',
                                  `Cost / ${singular}`,
                                  formatContribution(
                                      {
                                          key: contribution.resultActionType,
                                          label: singular,
                                          costPerResultMicros: contribution.costPerResultMicros,
                                      },
                                      currency,
                                  ),
                              )),
                        key: `costPerResult:${contribution.resultActionType}`,
                        tier: resultTier,
                        tooltip: tip(
                            `${singular} spend ÷ ${singular} count.`,
                            "Split per objective: this creative's ads optimise for different results, and a lead, a ThruPlay and a purchase are not on one scale.",
                            contribution.costPerResultMicros === null &&
                                `No ${singular} yet — the objective has spent but not converted.`,
                        ),
                    },
                )
            }
        } else {
            metrics.push({
                ...(windowMatchesGrid
                    ? gridMetric('costPerResult', row, currency, toneContext)
                    : detailMetric(
                          'costPerResult',
                          'Cost / result',
                          pending ? null : formatMoney(outcome?.costPerResultMicros, currency),
                      )),
                tier: resultTier,
                tooltip: tip(
                    'Spend ÷ results.',
                    // Reachable when a row says its objectives differ but carries no breakdown to
                    // split — a rolling deploy, or a detail response cached before the field existed.
                    // Without this the withheld figure reads as an unmeasured one.
                    mixedObjectives &&
                        "Withheld: this creative's ads optimise for different results, and a lead, a ThruPlay and a purchase are not on one scale.",
                ),
            })
        }
    }

    return { key: 'outcome', label: 'Outcome', metrics }
}

/**
 * The creative's own count of the account's terminal conversion event.
 *
 * A purchase advertiser reads the grid's own purchase column, so the panel can never present a count
 * that disagrees with the card it was opened from. Anything else is matched out of the conversions
 * block, which is keyed on the same catalog the terminal event came from.
 */
function terminalCountOf(
    row: ICreativePerformanceRow,
    outcome: ICreativeDetail['outcome'] | null,
    detail: ICreativeDetail | null | undefined,
): number {
    const model = detail?.outcomeModel
    if (!model || model.isPurchase) return purchasesOf(row, outcome)
    const match = detail?.conversions?.find((c) => c.actionType === model.terminalActionType)
    return match?.count ?? 0
}

/** `of clicks` / `of arrivals` — the suffix that states what the conversion rate is a share of. */
function conversionRateBasisLabel(detail: ICreativeDetail | null | undefined) {
    const basis = detail?.funnel?.conversionRateBasis
    if (basis === 'landingPageViews') return 'of arrivals'
    if (basis === 'outboundClicks') return 'of clicks'
    return null
}

/** The denominator named in full, for the metric's own tooltip. */
function conversionRateBasisPhrase(detail: ICreativeDetail | null | undefined) {
    const basis = detail?.funnel?.conversionRateBasis
    return basis === 'outboundClicks' ? 'outbound clicks' : 'landing page views'
}

/** Spend ÷ the terminal event's own count, from the conversions read that already computed it. */
function terminalCostMicros(detail: ICreativeDetail | null | undefined, model: IPanelOutcomeModel) {
    return detail?.conversions?.find((c) => c.actionType === model.terminalActionType)?.costPerMicros ?? null
}

/** How delivery went. Every figure is a hover away from its caveat; none of them shouts. */
function deliveryGroup({
    row,
    totals,
    currency,
    band,
    windowMatchesGrid,
    toneContext,
}: {
    row: ICreativePerformanceRow
    totals: ICreativeDetail['totals'] | null
    currency: string
    band: (key: Parameters<typeof evaluateBand>[0], value: number | null | undefined) => ReturnType<typeof evaluateBand>
    windowMatchesGrid: boolean
    toneContext: IMetricToneContext
}): IDetailMetricGroup {
    const cpmMicros = totals?.cpmMicros ?? row.cpmMicros
    const cpmBadge = band('cpm', cpmMicros ? Number(cpmMicros) / 1_000_000 : null)
    const outboundCtrValue = windowMatchesGrid
        ? (row.outboundCtr ?? null)
        : (totals?.outboundCtr ?? null)
    const outboundCtrBadge = band('outboundCtr', outboundCtrValue)
    // Same W/B ramp the card paints — stamp the window's CPM onto the row so a widened panel
    // window does not ramp against the grid's stale number.
    const cpmRamp = rampForColumn(
        'cpm',
        stampToneContext({ ...row, cpmMicros: cpmMicros ?? null }, toneContext),
    )
    return {
        key: 'delivery',
        label: 'Delivery',
        metrics: [
            windowMatchesGrid
                ? { ...gridMetric('impressions', row, currency, toneContext), tier: 'detail' as const }
                : detailMetric('impressions', 'Impressions', totals ? formatCount(totals.impressions) : null, {
                      tier: 'detail',
                  }),
            // Exact where the range-reach lane has a row for this window — Meta's reach de-duplicated across
            // exactly this creative's ads, the same figure the card shows. Bounds only where it has none
            // (a custom window, "last active", or a creative the lane has not fetched yet).
            totals?.windowReach
                ? detailMetric('reach', 'Reach', formatCount(totals.windowReach), {
                      tier: 'detail',
                      tooltip: 'People reached in this window, de-duplicated by Meta across all of this creative’s ads.',
                  })
                : detailMetric('reach', 'Reach', totals ? boundedCount(totals.reachUpperBound, 'at most') : null, {
                      tier: 'detail',
                      tooltip:
                          'An UPPER bound: the warehouse counts a person once per day reached, so summing overstates a long window. No de-duplicated reach exists for this window yet.',
                  }),
            totals?.windowFrequency != null
                ? detailMetric('frequency', 'Frequency', `${totals.windowFrequency.toFixed(2)}x`, {
                      tier: 'detail',
                      tooltip: 'Impressions ÷ people reached (de-duplicated across the creative’s ads) — the same figure as the card.',
                  })
                : detailMetric('frequency', 'Frequency', totals ? boundedRatio(totals.frequencyLowerBound, 'at least') : null, {
                      tier: 'detail',
                      tooltip:
                          'Impressions ÷ reach, and therefore a FLOOR — reach is over-stated, so this can only understate saturation. A high floor is a real finding; a low one licenses nothing.',
                  }),
            detailMetric('cpm', 'CPM', totals || windowMatchesGrid ? formatMoney(cpmMicros, currency) : null, {
                tier: 'detail',
                tooltip: bandTip('Spend per thousand impressions.', cpmBadge),
                tone: cpmBadge?.tone ?? 'neutral',
                reason: cpmBadge?.reason,
                ramp: cpmRamp,
                source: windowMatchesGrid ? 'row' : 'detail',
            }),
            // Outbound CTR is a default card metric — it must live on the panel, not only on the grid.
            windowMatchesGrid
                ? {
                      ...gridMetric('outboundCtr', row, currency, toneContext),
                      tier: 'detail' as const,
                      tooltip: bandTip('Outbound clicks ÷ impressions.', outboundCtrBadge),
                  }
                : detailMetric('outboundCtr', 'Outbound CTR', totals ? formatRate(totals.outboundCtr) : null, {
                      tier: 'detail',
                      tone: outboundCtrBadge?.tone ?? 'neutral',
                      reason: outboundCtrBadge?.reason,
                      ramp: rampForColumn(
                          'outboundCtr',
                          stampToneContext({ ...row, outboundCtr: outboundCtrValue }, toneContext),
                      ),
                      tooltip: bandTip('Outbound clicks ÷ impressions.', outboundCtrBadge),
                  }),
            windowMatchesGrid
                ? { ...gridMetric('outboundClicks', row, currency, toneContext), tier: 'detail' as const }
                : detailMetric('outboundClicks', 'Outbound clicks', totals ? formatCount(totals.outboundClicks) : null, {
                      tier: 'detail',
                      tooltip: 'Clicks that left Meta — the honest traffic number.',
                  }),
        ],
    }
}

/** What about the creative explains the numbers above. Video only. */
function diagnosticsGroup({
    totals,
    band,
    isVideoFormat,
    row,
    toneContext,
}: {
    totals: ICreativeDetail['totals'] | null
    band: (key: Parameters<typeof evaluateBand>[0], value: number | null | undefined) => ReturnType<typeof evaluateBand>
    /** False for a CAROUSEL/COLLECTION/FLEXIBLE ad that merely carries a video asset. */
    isVideoFormat: boolean
    row: ICreativePerformanceRow
    toneContext: IMetricToneContext
}): IDetailMetricGroup {
    const hook = band('hookRate', totals?.hookRate)
    const hold = band('holdRate', totals?.holdRate)
    const h2c = band('hookToClick', totals?.hookToClickRate)
    const rampRow = stampToneContext(
        {
            ...row,
            hookRate: totals?.hookRate ?? row.hookRate,
            holdRate: totals?.holdRate ?? row.holdRate,
        },
        toneContext,
    )

    return {
        key: 'diagnostics',
        label: 'Creative diagnostics',
        note: "These explain performance — they do not judge it. A creative can hold badly and still return well.",
        metrics: [
            detailMetric('hookRate', 'Hook rate', totals ? formatRate(totals.hookRate) : null, {
                tone: hook?.tone ?? 'neutral',
                reason: hook?.reason,
                ramp: rampForColumn('hookRate', rampRow),
                tooltip: bandTip(
                    '3-second plays ÷ impressions — how much of the delivered audience it stopped.',
                    hook,
                ),
            }),
            detailMetric('holdRate', 'Hold rate', totals ? formatRate(totals.holdRate) : null, {
                tone: hold?.tone ?? 'neutral',
                reason: hold?.reason,
                ramp: rampForColumn('holdRate', rampRow),
                tooltip: bandTip(
                    'ThruPlays ÷ 3-second plays — how many of the people it stopped it then kept.',
                    hold,
                ),
            }),
            // WITHHELD, not shown neutral, on a mixed-format ad: outbound clicks span every rendition
            // while three-second plays fire only on the video one, so the ratio routinely exceeds 100%.
            detailMetric(
                'hookToClick',
                'Hook-to-click',
                !isVideoFormat ? UNAVAILABLE : totals ? formatRate(totals.hookToClickRate) : null,
                {
                    tone: isVideoFormat ? (h2c?.tone ?? 'neutral') : 'neutral',
                    reason: isVideoFormat ? h2c?.reason : undefined,
                    source: !isVideoFormat ? 'row' : 'detail',
                    tooltip: isVideoFormat
                        ? bandTip('Outbound clicks ÷ 3-second plays.', h2c)
                        : 'Not measured: this ad serves non-video renditions too, so clicks and plays count different populations and the ratio is not a rate.',
                },
            ),
        ],
    }
}

/**
 * The one line Delivery collapses to. CPM and frequency are the two figures worth surfacing without
 * a click — the rest sits behind the disclosure, because a buyer opening a creative is not asking
 * about reach first and a panel that shouts every number shouts none of them.
 */
export function deliverySummary(group: IDetailMetricGroup): string | null {
    const pick = (key: string) => group.metrics.find((m) => m.key === key)?.value
    const parts = [pick('cpm') && `CPM ${pick('cpm')}`, pick('frequency') && `frequency ${pick('frequency')}`]
    const stated = parts.filter(Boolean)
    return stated.length ? stated.join(' · ') : null
}


/** One journey the account measures, as the server serves it. */
export type IPanelFunnelDestination = NonNullable<ICreativeDetail['funnel']>['destinations'][number]

/**
 * The band that judges the step INTO a stage, or `null` where none is published.
 *
 * Three bands exist and no more, and EACH names the pair it was calibrated on — so every one checks
 * the row rendered above as well as the row it sits on. A band quoted over a different denominator is
 * worse than no band: `adds to cart ÷ landing page views` stated over `÷ content views` reads as a
 * verdict on a ratio nobody measured. Inventing a band for a trial or a contact would do the same, so
 * those steps are stated with no verdict and the chart's one accent keeps its meaning.
 */
function stepBandKey(stage: IPanelFunnelDestination['stages'][number], previousKey: string | undefined) {
    if (stage.key === 'omni_landing_page_view' && previousKey === 'outboundClicks') return 'landingPageViewRate'
    if (stage.key === 'omni_add_to_cart' && previousKey === 'omni_landing_page_view') return 'addToCartRate'
    if (stage.isDestination && stage.key === 'omni_purchase' && previousKey === 'omni_initiated_checkout') {
        return 'checkoutCompletionRate'
    }
    return null
}

/**
 * The funnel as chart stages: a count, a bar width, and the step-through rate against the stage above.
 *
 * The SHAPE is the server's — which rows exist, in what order, with which rates — because the same
 * derivation feeds the agent's creative-details tool, and two copies of "what precedes a trial" is
 * how the two came to disagree. This maps that shape onto the chart's vocabulary: labels, the
 * one-hover tooltips, and the single accent.
 *
 * Only the stage that FAILS its band is marked as the leak — the chart's one accent. Marking every
 * stage that is merely below some ideal would put orange on most creatives and make the colour mean
 * nothing, and a stage whose event has no published band is never a candidate at all.
 */
export function buildFunnelStages({
    totals,
    destination,
    currency,
    band,
    model,
}: {
    totals: ICreativeDetail['totals'] | null
    /** The selected journey, already assembled server-side. `null` renders no funnel. */
    destination: IPanelFunnelDestination | null | undefined
    currency: string
    /** The band evaluator, so every stage can say what shape would have been healthy. */
    band: (key: Parameters<typeof evaluateBand>[0], value: number | null | undefined) => ReturnType<typeof evaluateBand>
    /** What this advertiser's outcome is; absent falls back to the e-commerce assumption. */
    model?: IPanelOutcomeModel
}): IFunnelStage[] | null {
    if (!totals || !destination) return null
    const outcome = model ?? ASSUMED_PURCHASE_MODEL

    /**
     * A low arrival rate is only the CREATIVE's leak where the arrival count is believable.
     *
     * `landing_page_view` comes from the browser pixel, so a consent-gated site loses it for every
     * non-consenting visitor. Marking that as a leak accuses the landing page of a collapse that is
     * an artifact of the measurement — so where the model has judged the count not credible, the
     * stage discloses the doubt and carries no accent.
     */
    const arrivalMeasurable = outcome.landingPageViewsCredible
    /**
     * The same judgement one step down, for a different failure.
     *
     * `InitiateCheckout` fires from the checkout page, so a store whose express checkout (Shop Pay,
     * Apple Pay, PayPal from the cart drawer) never renders one loses the event for every buyer who
     * takes that path while the purchase still fires. Accenting the completion rate then accuses a
     * checkout that is working, and sends the reader to fix a flow whose only fault is that we cannot
     * see it — the single most actionable thing this panel says, pointed at the wrong thing.
     */
    const checkoutMeasurable = outcome.initiatedCheckoutsCredible !== false
    const failsBand = (key: ICreativeBandKey, rate: number | null) => {
        if (rate === null || rate > 1) return false
        if (key === 'landingPageViewRate') {
            return arrivalMeasurable && rate < FUNNEL_THRESHOLDS.landingPageViewRate.healthyFrom
        }
        if (key === 'checkoutCompletionRate') {
            return checkoutMeasurable && rate < FUNNEL_THRESHOLDS.checkoutCompletion.healthyFrom
        }
        return false
    }

    // The one accent, chosen ACROSS the funnel rather than per stage: a checkout that collapses is
    // the more actionable finding, so it wins where both would fire.
    const leakCandidates = destination.stages.map((stage, i) => {
        const key = stepBandKey(stage, destination.stages[i - 1]?.key)
        return key && failsBand(key, stage.stepThroughRate) ? key : null
    })
    const checkoutLeak = leakCandidates.indexOf('checkoutCompletionRate')
    const leakIndex = checkoutLeak >= 0 ? checkoutLeak : leakCandidates.findIndex((key) => key !== null)

    return destination.stages.map((stage, i) => {
        const previous = destination.stages[i - 1]
        const bandKey = stepBandKey(stage, previous?.key)
        const label = stageLabel(stage)
        const isLeak = i === leakIndex

        return {
            key: stage.key,
            label,
            shortLabel: STAGE_SHORT_LABELS[stage.key] ?? label,
            value: stage.count === null ? UNAVAILABLE : formatCount(stage.count),
            count: stage.count ?? 0,
            rate: stage.stepThroughRate === null ? undefined : formatShare(stage.stepThroughRate),
            /**
             * A caption that belongs to THIS stage alone. CTR is a property of the click stage
             * (clicks ÷ impressions), NOT a step-through into the next one — putting it on `rate`
             * made it read as a drop.
             */
            context:
                stage.key === 'outboundClicks' && totals.outboundCtr !== null
                    ? `${formatShare(totals.outboundCtr)} of impressions (CTR)`
                    : undefined,
            isLeak,
            // Over 100% the two counts measure different populations, so the bar is capped and
            // hatched rather than drawn wider than the stage it came from.
            exceedsPrevious: stage.exceedsPrevious,
            tooltip: stageTooltip({
                stage,
                previous,
                label,
                bandKey,
                currency,
                band,
                arrivalMeasurable,
                checkoutMeasurable,
                isLeak,
                outboundCtr: totals.outboundCtr,
            }),
        }
    })
}

/** The row's name: a delivery figure at the head, the advertiser's own event everywhere else. */
function stageLabel(stage: IPanelFunnelDestination['stages'][number]) {
    if (stage.key === 'outboundClicks') return 'Outbound clicks'
    if (stage.key === 'impressions') return 'Impressions'
    return resultTypeLabel(stage.key) || stage.key
}

/**
 * A ≤12-character form for the steps a narrow host would otherwise wrap.
 *
 * Only the fixed path steps have one. A DESTINATION is the advertiser's own event and can be
 * anything ("Website Start Trials", a custom conversion's own name), so abbreviating it would mean
 * inventing a name for an event we did not name.
 */
const STAGE_SHORT_LABELS: Readonly<Record<string, string>> = {
    outboundClicks: 'Clicks',
    impressions: 'Impressions',
    omni_landing_page_view: 'LP views',
    omni_add_to_cart: 'Adds to cart',
    omni_initiated_checkout: 'Checkouts',
    omni_complete_registration: 'Sign-ups',
    start_trial_website: 'Trials',
}

/**
 * Everything about one stage in a single hover: what it counts, the formula for the step into it,
 * the band it is read against, its cost per one, and any reason it reads `—` or is capped.
 */
function stageTooltip({
    stage,
    previous,
    label,
    bandKey,
    currency,
    band,
    arrivalMeasurable,
    checkoutMeasurable,
    isLeak,
    outboundCtr,
}: {
    stage: IPanelFunnelDestination['stages'][number]
    previous: IPanelFunnelDestination['stages'][number] | undefined
    label: string
    bandKey: ICreativeBandKey | null
    currency: string
    /** The creative's own outbound CTR — the value the head's band is placed against. */
    outboundCtr: number | null
    band: (key: Parameters<typeof evaluateBand>[0], value: number | null | undefined) => ReturnType<typeof evaluateBand>
    arrivalMeasurable: boolean
    /** Whether the account's checkout starts are credibly instrumented. */
    checkoutMeasurable: boolean
    isLeak: boolean
}) {
    if (!stage.reported) {
        return `This account sends no ${label.toLowerCase()} events, so there is nothing to count here — it is not a zero.`
    }
    if (stage.key === 'outboundClicks') {
        return bandTip('Clicks that left Meta ÷ impressions.', band('outboundCtr', outboundCtr))
    }
    if (stage.key === 'impressions') {
        return 'Times this creative was shown. The journey starts here because this conversion is completed on Meta — no outbound click precedes it.'
    }

    const formula = previous ? `${label} ÷ ${stageLabelOf(previous)}.` : null
    const arrivalDoubt =
        stage.key === 'omni_landing_page_view' && !arrivalMeasurable
            ? 'This account reports far fewer arrivals than the steps around them imply — typically a consent-gated pixel, which loses the arrival for every visitor who does not consent while the conversions below it survive. Read this as an unreliable measurement, not as a broken landing page.'
            : null
    /**
     * The doubt sits on the CHECKOUT row and on the row whose rate divides by it, because those are
     * the two figures the under-count moves. Named on both so a reader hovering either one is told the
     * same thing, rather than having to find the row that happens to carry the disclosure.
     */
    const checkoutDoubt =
        !checkoutMeasurable && (stage.key === 'omni_initiated_checkout' || previous?.key === 'omni_initiated_checkout')
            ? 'This account reports far fewer checkout starts than the steps around them imply — typically an express checkout (Shop Pay, Apple Pay, PayPal) that fires no InitiateCheckout while the purchase still fires server-side. Read this as under-firing instrumentation to fix in the pixel, not as a broken checkout flow.'
            : null

    const stepBand = bandKey && !arrivalDoubt && !checkoutDoubt ? band(bandKey, stage.stepThroughRate) : null

    return tip(
        formula,
        stepBand ? { label: 'Band', body: stepBand.badge } : null,
        arrivalDoubt,
        checkoutDoubt,
        stage.costPerMicros ? `Cost per ${singularOf(stage.key)} ${formatMoney(stage.costPerMicros, currency)}.` : null,
        // The only step-specific advice the panel carries, because it is the only one that redirects
        // the reader AWAY from the creative: a checkout that collapses is not the ad's fault.
        //
        // `checkoutMeasurable` is stated here as well as in `failsBand`, which already withholds the
        // accent. Two gates for one sentence is deliberate: this is the panel's single most actionable
        // claim, and leaving its suppression to ride on `isLeak` would let any future change to how the
        // accent is chosen quietly restore it against a checkout count we do not believe.
        isLeak && checkoutMeasurable && bandKey === 'checkoutCompletionRate'
            ? 'Well under the healthy range: check the checkout flow before iterating the ad.'
            : null,
        !previous?.reported && previous
            ? `The step above is not measured on this account, so there is no rate into ${label.toLowerCase()}.`
            : null,
        stage.exceedsPrevious
            ? 'Over 100%, so this is not a step-through: the two counts span different populations rather than describing one journey. The bar is capped.'
            : null,
    )
}

/** The label of a neighbouring stage, phrased for use inside a formula. */
function stageLabelOf(stage: IPanelFunnelDestination['stages'][number]) {
    return stageLabel(stage).toLowerCase()
}

/** "add to cart" from `omni_add_to_cart` — the singular a per-unit cost reads with. */
function singularOf(actionType: string) {
    return costPerResultTypeLabel(actionType).replace(/^Per /, '').toLowerCase() || actionType
}

/**
 * What the creative actually ACHIEVED, in the advertiser's own conversion events.
 *
 * This section exists because the panel used to present exactly one conversion number — the ad set's
 * own `result_action_type` — and render everything else as absent. On a lead or subscription
 * advertiser that turned €1,096 of spend into "1 result", when the same creative had in fact driven
 * 11 contacts, a registration, a trial and a subscription. The counts were in the warehouse the whole
 * time; nothing read them.
 *
 * The events are DISCOVERED from the account, ranked by the account's own volume, so the advertiser's
 * primary outcome leads — and a creative that drove none of it still shows a zero against it, which is
 * itself the finding.
 */
function conversionsGroup({
    conversions,
    currency,
    isLoading,
}: {
    conversions: ICreativeDetail['conversions'] | null
    currency: string
    isLoading: boolean
}): IDetailMetricGroup | null {
    if (!conversions) {
        // Only skeleton while the read is genuinely in flight; an account with no conversion events at
        // all gets no section rather than an empty one.
        return isLoading ? { key: 'conversions', label: 'Conversions', metrics: [] } : null
    }
    if (conversions.length === 0) return null

    return {
        key: 'conversions',
        label: 'Conversions',
        note: "The events this advertiser actually fires, ranked by how much the account uses them — not just the one goal this ad set optimises for.",
        metrics: conversions.map((conversion, index) => ({
            key: `conv:${conversion.actionType}`,
            label: resultTypeLabel(conversion.actionType),
            value: formatCount(conversion.count),
            tone: 'neutral' as const,
            // The account's top two outcomes lead; the tail is real but quieter.
            tier: (index < 2 ? 'hero' : 'supporting') as IDetailMetricTier,
            source: 'detail' as const,
            context:
                conversion.costPerMicros === null
                    ? undefined
                    : `${formatMoney(conversion.costPerMicros, currency)} each`,
            tooltip: tip(
                `${costPerResultTypeLabel(conversion.actionType)}: ${
                    conversion.costPerMicros === null
                        ? 'no cost per result — this creative drove none in the window'
                        : formatMoney(conversion.costPerMicros, currency)
                }.`,
                conversion.partialCoverage &&
                    `Meta only reports this event from ${conversion.firstDate}, so the count covers part of the window rather than all of it.`,
            ),
        })),
    }
}

/**
 * The funnel section's frame. Its FIGURES live in `buildFunnelStages` and render as a step chart —
 * this carries only the section's identity and the caveat that applies to the whole of it.
 *
 * The heading names the journey rather than the click, because the journey is now the thing that
 * varies: an on-Meta conversion has no click on its path at all, so "Click journey" would have been
 * a promise the chart does not keep. Which EVENT it ends at is named by the destination control
 * beside it, so the heading and the stages never compete to name the same thing.
 */
function funnelGroup(): IDetailMetricGroup {
    return {
        key: 'funnel',
        label: 'Journey',
        note: "Each ↓ is the share that reached the next step. A step on this journey the account does not fire shows —; steps that belong to a different conversion's journey are not shown at all. Meta's funnel counts are directional — compare creatives, not against an absolute rate.",
        metrics: [],
    }
}


/** `purchase value ÷ purchases` from the grid row, in micros — the row's own AOV, with no round-trip. */
function rowAovMicros(row: ICreativePerformanceRow): string | null {
    const purchases = Number(row.purchaseConversions ?? 0)
    if (!(purchases > 0)) return null
    return String(Math.round(Number(row.revenueMicros) / purchases))
}

function formatRoas(roas: number | null | undefined) {
    return roas === null || roas === undefined ? UNAVAILABLE : `${roas.toFixed(2)}x`
}


/** Past this, the campaign's spend is concentrated enough that pausing a top ad is a budget decision. */
export const TOP_ADS_DEPENDENCY_SHARE = 0.7

/**
 * "vs. account avg 210 €" — the comparison that turns an outlying AOV into an explanation. Omitted
 * rather than estimated when either side is unavailable. The "basket drives this return" clause
 * shares BOTH gates with `aovTone` (material multiple + conversion floor), so a neutral row can
 * never claim causation the colour withheld.
 */
function aovContext(
    aovMicros: string | null,
    accountAovMicros: string | null,
    terminalCount: number,
    currency: string
): string | undefined {
    if (!aovMicros || !accountAovMicros) return undefined
    const accountAov = Number(accountAovMicros)
    // The causal clause needs a MATERIAL gap AND enough conversions for the average to mean anything.
    // A bare `>` fires on roughly half of all creatives by construction; skipping the floor would
    // assert causation off a two-sale fluke while the tone stays neutral.
    const drivenByBasket =
        accountAov > 0 &&
        terminalCount >= VERDICT_PURCHASES_FLOOR &&
        Number(aovMicros) / accountAov >= AOV_MATERIAL_MULTIPLE
    return `vs. account avg ${formatSpend(accountAov, currency)}${drivenByBasket ? ' — basket drives this return' : ''}`
}

/**
 * The AOV soft verdict — the registry's "colour softly" rule, shared by the table column and the
 * panel's Outcome row so both surfaces call the same basket at the same threshold.
 *
 * `good`-only by design: an above-average basket is worth a glance because it EXPLAINS a return
 * (basket, not creative), while a below-average one is ordinary composition — punishing it would
 * grade half of every account's creatives red by construction. The threshold is the same material
 * multiple `aovContext` uses for its "basket drives this return" clause, and both share the
 * conversion floor, so the colour and the sentence can never disagree. `null` when either side of
 * the comparison is missing.
 */
export function aovTone(
    aovMicros: string | null | undefined,
    accountAovMicros: string | number | null | undefined,
    /** The row's own count of the terminal event — the same floor every money verdict gates on. */
    terminalCount: number,
    currency: string
): IMetricToneResult | null {
    if (aovMicros === null || aovMicros === undefined) return null
    const accountAov = Number(accountAovMicros ?? 0)
    if (!(accountAov > 0)) return null
    if (!(terminalCount >= VERDICT_PURCHASES_FLOOR)) {
        return {
            tone: 'neutral',
            reason: `Only ${terminalCount} ${terminalCount === 1 ? 'conversion' : 'conversions'} — too few for the average to mean anything (needs ${VERDICT_PURCHASES_FLOOR}).`,
        }
    }
    if (Number(aovMicros) / accountAov >= AOV_MATERIAL_MULTIPLE) {
        return {
            tone: 'good',
            reason: `At least ${AOV_MATERIAL_MULTIPLE}x the account average (${formatSpend(accountAov, currency)}) — the basket, not the creative, may drive this row's return.`,
        }
    }
    return {
        tone: 'neutral',
        reason: `Around the account average (${formatSpend(accountAov, currency)}) — basket size does not explain this row.`,
    }
}


/** A bounded count — "at most 41.1K" — so the reader never mistakes the bound for a measurement. */
function boundedCount(value: string | null | undefined, qualifier: string) {
    if (value === null || value === undefined) return UNAVAILABLE
    return `${qualifier} ${formatCount(value)}`
}

/** A bounded ratio — "at least 1.18x". */
function boundedRatio(value: number | null | undefined, qualifier: string) {
    if (value === null || value === undefined) return UNAVAILABLE
    return `${qualifier} ${value.toFixed(2)}x`
}

/** One funnel rate, formatted, or `null` while the read is in flight. */
function funnelRate(detail: ICreativeDetail | null | undefined, key: 'conversionRate') {
    return detail?.funnel ? formatRate(detail.funnel[key]) : null
}

/** Bounded step through the grid's current result set — the sweep the arrow keys drive. */
export function stepIndex(current: number, direction: -1 | 1, total: number) {
    const next = current + direction
    return next >= 0 && next < total ? next : null
}
